import { useState } from 'react'
import { useOnboarding } from '../../contexts/OnboardingContext'

export interface BusinessTypeStepProps {
  onNext: () => void
}

interface BusinessTypeOption {
  value: string
  label: string
  description: string
}

const BUSINESS_TYPES: BusinessTypeOption[] = [
  {
    value: 'sole_proprietorship',
    label: 'Sole Proprietorship',
    description: 'A business owned and operated by a single individual',
  },
  {
    value: 'partnership',
    label: 'Partnership',
    description: 'A business owned by two or more individuals',
  },
  {
    value: 'private_limited',
    label: 'Private Limited',
    description: 'A privately held company with limited liability',
  },
  {
    value: 'public_limited',
    label: 'Public Limited',
    description: 'A publicly traded company with limited liability',
  },
  {
    value: 'llp',
    label: 'LLP',
    description: 'A Limited Liability Partnership combining partnership and corporate benefits',
  },
]

/**
 * BusinessTypeStep renders 5 business type cards for the user to select from.
 * Selection is persisted in OnboardingContext and preserved on back navigation.
 */
export function BusinessTypeStep({ onNext }: BusinessTypeStepProps) {
  const { state, dispatch } = useOnboarding()
  const [validationError, setValidationError] = useState<string | null>(null)

  const selectedType = state.businessType

  const handleSelect = (value: string) => {
    dispatch({ type: 'SET_BUSINESS_TYPE', payload: value })
    setValidationError(null)
  }

  const handleNext = () => {
    if (!selectedType) {
      setValidationError('Please select a business type to continue')
      return
    }
    onNext()
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
        Select your business type
      </h2>
      <p className="text-secondary-500 mb-6">
        Choose the legal structure that best describes your business.
      </p>

      {/* Business type cards */}
      <div
        role="radiogroup"
        aria-label="Business type selection"
        className="grid gap-3"
      >
        {BUSINESS_TYPES.map((type) => {
          const isSelected = selectedType === type.value

          return (
            <button
              key={type.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={type.label}
              onClick={() => handleSelect(type.value)}
              className={`
                w-full text-left p-4 rounded-lg border-2 transition-all
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                ${isSelected
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-secondary-200 bg-white hover:border-secondary-300 hover:bg-secondary-50'
                }
              `}
            >
              <div className="flex items-center gap-3">
                {/* Radio indicator */}
                <span
                  className={`
                    flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${isSelected
                      ? 'border-primary-600'
                      : 'border-secondary-300'
                    }
                  `}
                  aria-hidden="true"
                >
                  {isSelected && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary-600" />
                  )}
                </span>

                <div>
                  <span className="block text-sm font-medium text-secondary-900">
                    {type.label}
                  </span>
                  <span className="block text-xs text-secondary-500 mt-0.5">
                    {type.description}
                  </span>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Validation error */}
      {validationError && (
        <p
          role="alert"
          className="mt-4 text-sm text-red-600"
        >
          {validationError}
        </p>
      )}

      {/* Next button */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={handleNext}
          disabled={!selectedType}
          className={`
            px-6 py-2.5 rounded-lg text-sm font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${selectedType
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
            }
          `}
        >
          Next
        </button>
      </div>
    </div>
  )
}
